import DOMPurify from 'dompurify';

// Helper function to sanitize HTML content to prevent XSS attacks
export const sanitizeHTML = (html: string): string => {
  // Configure DOMPurify to allow scripts from our trusted cloned content
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'script') {
      // Allow script tags from our content
      const scriptContent = node.textContent || '';
      // Allow scripts with specific patterns (board, jQuery, common UI functionality)
      if (scriptContent.includes('lopen_') ||
          scriptContent.includes('expandDiv_') ||
          scriptContent.includes('jQuery') ||
          scriptContent.includes('g5_url') ||
          scriptContent.includes('board_move') ||
          scriptContent.includes('btn_more_opt') ||
          scriptContent.includes('cmt_btn') ||
          scriptContent.includes('char_min') ||
          scriptContent.includes('view_image') ||
          scriptContent.includes('viewimageresize')) {
        data.allowedTags['script'] = true;
      }
    }
  });

  return DOMPurify.sanitize(html, {
    // Allow common tags needed for content
    ALLOWED_TAGS: [
      'div', 'p', 'span', 'a', 'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'br', 'strong', 'em', 'b', 'i', 'u', 'section', 'article', 'nav',
      'form', 'input', 'button', 'select', 'option', 'textarea', 'label',
      'iframe', 'video', 'audio', 'source', 'script', 'colgroup', 'col', 'caption'
    ],
    // Allow necessary attributes
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'class', 'id', 'style', 'target', 'rel',
      'width', 'height', 'title', 'data-*', 'aria-*', 'role',
      'type', 'name', 'value', 'placeholder', 'frameborder', 'allowfullscreen',
      'for', 'checked', 'rowspan', 'colspan', 'download'
    ],
    // Allow data attributes
    ALLOW_DATA_ATTR: true,
    // Allow aria attributes for accessibility
    ALLOW_ARIA_ATTR: true,
    // Force allow scripts
    ADD_TAGS: ['script'],
    ADD_ATTR: ['type'],
    FORCE_BODY: true,
    KEEP_CONTENT: true
  });
};

// Helper function to handle link clicks in cloned pages
export const handlePageLinkClick = (
  e: Event,
  navigate: (path: string) => void
) => {
  const target = e.target as HTMLElement;
  const link = target.closest('a');

  if (link) {
    const href = link.getAttribute('href');

    // Check if this is a toggle button (lopen_01, lopen_02, lopen_03, etc.)
    const isToggleButton = link.classList.contains('lopen_01') ||
                          link.classList.contains('lopen_02') ||
                          link.classList.contains('lopen_03') ||
                          link.className.includes('lopen_');

    if (isToggleButton) {
      // Let the toggle button work without interference
      return;
    }

    // Check if this is a file download link
    const isDownloadLink = link.classList.contains('view_file_download') ||
                          link.hasAttribute('download') ||
                          (href && (href.endsWith('.hwp') || href.endsWith('.pdf') ||
                                   href.endsWith('.zip') || href.endsWith('.doc') ||
                                   href.endsWith('.docx') || href.endsWith('.xls') ||
                                   href.endsWith('.xlsx')));

    if (isDownloadLink) {
      // Let download links work without interference
      return;
    }

    if (!href) {
      // Links without href (dropdown headers, etc.) - prevent default but don't navigate
      e.preventDefault();
      return;
    }

    if (href === '#' || href === '#none') {
      // Ignore hash links
      e.preventDefault();
      return;
    }

    // Check if it's an external link that should be allowed (koelsa.or.kr, etc.)
    const isExternalAllowed =
      href.startsWith('http') &&
      !href.includes('www.keso.kr') &&
      !href.includes('://keso.kr');

    if (isExternalAllowed) {
      // Let external links work normally
      return;
    }

    // Prevent default for all keso.kr links and internal links
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation(); // Prevent other handlers (like nav.js) from running

    // Check if it's a keso.kr link (our cloned site)
    if (href.includes('www.keso.kr') || href.includes('://keso.kr')) {
      try {
        // Extract the path from keso.kr URL
        const url = new URL(href.startsWith('http') ? href : 'http://www.keso.kr' + href);
        const path = url.pathname;

        // Navigate to internal route
        if (path && path !== '/') {
          navigate(path);
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Error parsing URL:', error);
      }
    } else if (href.startsWith('/') && !href.startsWith('//')) {
      // Internal link (relative path)
      navigate(href);
    } else if (href.endsWith('.html') || !href.includes('/') && !href.includes('.')) {
      // Handle relative HTML file links (e.g., "message.html", "org.html")
      // Also handle links without extensions (e.g., "law1", "law2")
      const fileName = href.split('/').pop()?.replace('.html', '');

      // Map HTML files to routes based on common patterns
      const routeMap: { [key: string]: string } = {
        // Company pages
        'about': '/company/about',
        'message': '/company/message',
        'vision': '/company/vision',
        'history': '/company/history',
        'org': '/company/org',
        'ci': '/company/ci',
        'contribution': '/company/contribution',
        'map': '/company/map',

        // Business pages
        'b1': '/business/b1',
        'b2': '/business/b2',
        'b3': '/business/b3',
        'b4': '/business/b4',

        // Check pages
        'c1': '/check/c1',
        'c3': '/check/c3',

        // Info pages
        'law': '/law',
        'law1': '/law1',
        'law2': '/law2',
        'law3': '/law3',
        'law4': '/law4',

        // Customer service pages
        'cs1': '/cs/cs1',
        'cs4': '/cs/cs4',
        'variousform': '/variousform',
        'qna': '/qna',
        'communication': '/communication',

        // News pages
        'notice': '/notice',
        'press': '/press',
        'announcement': '/announcement',
        'promotion': '/promotion',
        'companynotice': '/companynotice',
      };

      if (fileName && routeMap[fileName]) {
        navigate(routeMap[fileName]);
      } else {
        console.warn('Unknown HTML file:', href);
      }
    }
  }
};

// Helper function to fix image and asset paths
const fixAssetPaths = (content: string, sourceUrl: string): string => {
  // Create a temporary div to parse the content
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;

  // Determine the base path from the source URL
  // For example: /cloneTarget/www.keso.kr/cs/cs4.html -> /cloneTarget/www.keso.kr/cs/
  const urlParts = sourceUrl.split('/');
  urlParts.pop(); // Remove the file name
  const basePath = urlParts.join('/');

  // Fix all image src attributes
  const images = tempDiv.querySelectorAll('img[src]');
  images.forEach((img) => {
    const src = img.getAttribute('src');
    if (src && src.startsWith('../')) {
      // Convert relative path to absolute path
      // ../customized/images/... -> /cloneTarget/www.keso.kr/customized/images/...
      const relativePath = src.replace(/^\.\.\//, '');
      const absolutePath = `/cloneTarget/www.keso.kr/${relativePath}`;
      img.setAttribute('src', absolutePath);
    }
  });

  // Fix all CSS background-image URLs in style attributes
  const elementsWithStyle = tempDiv.querySelectorAll('[style]');
  elementsWithStyle.forEach((element) => {
    const style = element.getAttribute('style');
    if (style && style.includes('url(')) {
      const fixedStyle = style.replace(/url\(['"]?\.\.\/([^'")\s]+)['"]?\)/g,
        (match, path) => `url('/cloneTarget/www.keso.kr/${path}')`);
      element.setAttribute('style', fixedStyle);
    }
  });

  // Add center alignment to .tc class elements
  const centerElements = tempDiv.querySelectorAll('.tc');
  centerElements.forEach((element) => {
    const currentStyle = element.getAttribute('style') || '';
    element.setAttribute('style', `text-align: center; ${currentStyle}`);
  });

  // Center align specific images (business_01.jpg)
  const businessImages = tempDiv.querySelectorAll('img[src*="business_01"]');
  businessImages.forEach((img) => {
    // Wrap image in a div with center alignment if not already wrapped
    const parent = img.parentElement;
    if (parent && parent.tagName !== 'DIV' || (parent && !parent.style.textAlign)) {
      const wrapper = document.createElement('div');
      wrapper.style.textAlign = 'center';
      img.parentNode?.insertBefore(wrapper, img);
      wrapper.appendChild(img);
    }
  });

  return tempDiv.innerHTML;
};

// Helper function to load page content
export const loadPageContent = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const html = await response.text();

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  let fullContent = '';

  // Add sub visual
  const subVisual = doc.querySelector('#sub_visual_wrap');
  if (subVisual) {
    fullContent += subVisual.outerHTML;
  }

  // Add navigation bar
  const contentDiv = doc.querySelector('#content');
  if (contentDiv) {
    fullContent += contentDiv.outerHTML;
  }

  // Add main content body
  const conBody = doc.querySelector('.con_body');
  if (conBody) {
    fullContent += conBody.outerHTML;
  }

  // Fix asset paths before sanitization
  fullContent = fixAssetPaths(fullContent, url);

  // Remove Cloudflare email protection and restore original emails
  // Pattern 1: <a href="/cdn-cgi/l/email-protection" ...>[email protected]</a>
  fullContent = fullContent.replace(
    /<a[^>]*href="\/cdn-cgi\/l\/email-protection"[^>]*>.*?<\/a>/gi,
    '<span data-email="kesti2026" data-domain="daum.net"></span>'
  );

  // Pattern 2: __cf_email__ span tags
  fullContent = fullContent.replace(
    /<span[^>]*class="__cf_email__"[^>]*>.*?<\/span>/gi,
    '<span data-email="kesti2026" data-domain="daum.net"></span>'
  );

  // Pattern 3: Direct email text replacement (if not yet obfuscated)
  fullContent = fullContent.replace(
    /kesti2026@daum\.net/g,
    '<span data-email="kesti2026" data-domain="daum.net"></span>'
  );

  // Pattern 4: [email protected] text
  fullContent = fullContent.replace(
    /\[email&#160;protected\]/gi,
    '<span data-email="kesti2026" data-domain="daum.net"></span>'
  );

  // CRITICAL: Sanitize HTML before returning to prevent XSS attacks
  return sanitizeHTML(fullContent);
};
